/**
 * Types for the Booking System (Cal.com-style appointment scheduling)
 *
 * Public booking URL pattern: /book/:userSlug/:eventTypeSlug
 * Booker self-service: /booking/manage/:publicToken
 */

// ─── Enums / Unions ──────────────────────────────────────────

export type BookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show' | 'rescheduled';

/** Day of week: 0=Sunday, 1=Monday, ..., 6=Saturday */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

// ─── Custom Fields ───────────────────────────────────────────

export interface BookingCustomFieldDescriptor {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'email' | 'phone' | 'select' | 'checkbox' | 'number';
  required: boolean;
  options?: string[];
  placeholder?: string;
}

export type BookingCustomFieldAnswers = Record<string, string | boolean>;

// ─── Booking Settings ────────────────────────────────────────

export interface BookingSettings {
  id: string;
  organization_id: string;
  brand_color: string;
  logo_url?: string;
  /** Nome mostrato nelle pagine pubbliche al posto della ragione sociale (facoltativo) */
  display_name?: string | null;
  welcome_message?: string;
  timezone: string;
  allow_cancellation: boolean;
  cancellation_notice_minutes: number;
  cancellation_policy_text?: string;
  send_confirmation_email: boolean;
  send_org_notification_email: boolean;
  org_notification_email?: string;
  /** Se l'appuntamento non ha un cliente collegato, crea/aggancia il cliente dai dati del booker */
  auto_create_client_from_booker?: boolean;
  created_at: string;
  updated_at: string;
}

// ─── Booking Event Types ─────────────────────────────────────

export interface BookingEventType {
  id: string;
  organization_id: string;
  slug: string;
  title: string;
  description?: string;
  color: string;
  duration_minutes: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  min_notice_minutes: number;
  max_days_in_advance: number;
  assigned_user_id: string;
  create_meet_link: boolean;
  location?: string;
  confirmation_message?: string;
  cancellation_notice_minutes?: number;
  allow_reschedule: boolean;
  is_active: boolean;
  is_listed: boolean;
  timezone: string;
  custom_fields: BookingCustomFieldDescriptor[];
  slot_interval_minutes?: number | null;
  requires_confirmation: boolean;
  max_bookings_per_day?: number | null;
  max_bookings_per_week?: number | null;
  max_bookings_per_month?: number | null;
  max_bookings_per_slot?: number | null;
  success_redirect_url?: string | null;
  allow_cancellation: boolean;
  forward_params_redirect: boolean;
  reschedule_notice_minutes?: number | null;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Joined
  assigned_user?: { id: string; email: string; full_name?: string; avatar_url?: string; booking_slug?: string };
  /** Multi-user assignments (junction table) */
  assigned_users?: Array<{ id: string; email: string; full_name?: string; avatar_url?: string; booking_slug?: string }>;
}

export interface BookingEventTypeFormData {
  slug: string;
  title: string;
  description?: string;
  color?: string;
  duration_minutes: number;
  buffer_before_minutes?: number;
  buffer_after_minutes?: number;
  min_notice_minutes?: number;
  max_days_in_advance?: number;
  create_meet_link?: boolean;
  location?: string;
  confirmation_message?: string;
  cancellation_notice_minutes?: number;
  allow_reschedule?: boolean;
  is_active?: boolean;
  is_listed?: boolean;
  timezone?: string;
  custom_fields?: BookingCustomFieldDescriptor[];
  slot_interval_minutes?: number | null;
  requires_confirmation?: boolean;
  max_bookings_per_day?: number | null;
  max_bookings_per_week?: number | null;
  max_bookings_per_month?: number | null;
  max_bookings_per_slot?: number | null;
  success_redirect_url?: string | null;
  allow_cancellation?: boolean;
  forward_params_redirect?: boolean;
  reschedule_notice_minutes?: number | null;
  assigned_user_id?: string;
  assigned_user_ids?: string[];
}

// ─── Availability ────────────────────────────────────────────

export interface BookingAvailability {
  id: string;
  organization_id: string;
  user_id: string;
  event_type_id: string;
  day_of_week: DayOfWeek;
  start_time: string; // HH:MM
  end_time: string;   // HH:MM
  is_active: boolean;
  timezone: string;
  created_at: string;
  updated_at: string;
}

export interface BookingAvailabilityFormData {
  event_type_id?: string;
  day_of_week: DayOfWeek;
  start_time: string;
  end_time: string;
  is_active?: boolean;
  timezone?: string;
}

// ─── Date Overrides ──────────────────────────────────────────

export interface BookingDateOverrideWindow {
  id: string;
  override_id: string;
  start_time: string;
  end_time: string;
}

export interface BookingDateOverride {
  id: string;
  organization_id: string;
  user_id: string;
  event_type_id: string;
  override_date: string; // YYYY-MM-DD
  is_blocked: boolean;
  reason?: string;
  created_at: string;
  updated_at: string;
  windows?: BookingDateOverrideWindow[];
}

export interface BookingDateOverrideFormData {
  event_type_id?: string;
  override_date: string;
  is_blocked: boolean;
  reason?: string;
  windows?: { start_time: string; end_time: string }[];
}

// ─── Bookings ────────────────────────────────────────────────

export interface Booking {
  id: string;
  organization_id: string;
  event_type_id: string;
  assigned_to: string;
  start_time: string;
  end_time: string;
  booker_timezone: string;
  booker_name: string;
  booker_email: string;
  booker_phone?: string;
  booker_notes?: string;
  client_id?: string;
  status: BookingStatus;
  cancelled_at?: string;
  cancelled_by?: 'booker' | 'org' | 'system';
  cancellation_reason?: string;
  rescheduled_from_id?: string;
  rescheduled_to_id?: string;
  google_event_id?: string;
  google_calendar_id?: string;
  meet_link?: string;
  calendar_event_id?: string;
  confirmation_sent_at?: string;
  reminder_24h_sent_at?: string;
  reminder_1h_sent_at?: string;
  custom_field_answers: BookingCustomFieldAnswers;
  public_token: string;
  internal_notes?: string;
  created_at: string;
  updated_at: string;
  // Joined
  event_type?: Pick<BookingEventType, 'id' | 'title' | 'slug' | 'duration_minutes' | 'color'>;
  assigned_user?: { id: string; email: string; full_name?: string; avatar_url?: string };
  client?: { id: string; business_name: string; email?: string };
}

// ─── Public Booking API ──────────────────────────────────────

export interface BookingCreatePublicRequest {
  event_type_id: string;
  start_time: string;
  booker_name: string;
  booker_email: string;
  booker_phone?: string;
  booker_notes?: string;
  booker_timezone: string;
  custom_field_answers?: BookingCustomFieldAnswers;
  available_user_ids?: string[]; // team mode: users available for this slot
  /** Codice dell'invito, quando il link è stato mandato a un lead che già conosciamo. */
  invite_token?: string;
}

export interface BookingCreateInternalFormData extends BookingCreatePublicRequest {
  assigned_to?: string;
  client_id?: string;
  internal_notes?: string;
  /** Override fine appuntamento (per durata manuale). Se assente, calcolata dalla durata dell'event type. */
  end_time?: string;
}

// ─── Slot Computation ────────────────────────────────────────

export interface BookingSlot {
  start: string; // ISO 8601 UTC
  end: string;
  available?: boolean; // allSlots: true se libero, false se occupato (prenotazione/buffer/Google). Assente = libero
  available_user_ids?: string[]; // team mode: which users are available for this slot
}

export interface GetAvailableSlotsRequest {
  event_type_id: string;
  date: string; // YYYY-MM-DD
  timezone: string;
}

export interface GetAvailableSlotsResponse {
  slots: BookingSlot[];
  timezone: string;
}

// ─── Public Page Data ────────────────────────────────────────

export interface PublicBookingPageData {
  user: {
    id: string;
    full_name: string;
    avatar_url?: string;
    brand_color: string;
    logo_url?: string;
    welcome_message?: string;
    org_name: string;
  };
  event_types: Array<Pick<BookingEventType,
    'id' | 'slug' | 'title' | 'description' | 'color' | 'duration_minutes' | 'location' | 'custom_fields'
  >>;
}

export interface PublicEventTypeData {
  user: {
    id: string;
    full_name: string;
    avatar_url?: string;
    brand_color: string;
    logo_url?: string;
    org_name: string;
  };
  event_type: Pick<BookingEventType,
    'id' | 'slug' | 'title' | 'description' | 'color' | 'duration_minutes' |
    'location' | 'custom_fields' | 'allow_reschedule' | 'cancellation_notice_minutes' | 'confirmation_message' |
    'requires_confirmation' | 'success_redirect_url' | 'allow_cancellation'
  >;
  available_days_of_week?: number[];
  blocked_dates?: string[];
  override_available_dates?: string[];
}

// ─── Status Display Config ───────────────────────────────────

export const BOOKING_STATUS_CONFIG: Record<BookingStatus, {
  label: string;
  color: string;
  bgColor: string;
}> = {
  pending: { label: 'In attesa', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  confirmed: { label: 'Confermata', color: 'text-green-600', bgColor: 'bg-green-100' },
  cancelled: { label: 'Cancellata', color: 'text-red-600', bgColor: 'bg-red-100' },
  completed: { label: 'Completata', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  no_show: { label: 'Non presentato', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  rescheduled: { label: 'Riprogrammata', color: 'text-purple-600', bgColor: 'bg-purple-100' },
};

export const DAY_OF_WEEK_LABELS: Record<DayOfWeek, string> = {
  0: 'Domenica',
  1: 'Lunedi',
  2: 'Martedi',
  3: 'Mercoledi',
  4: 'Giovedi',
  5: 'Venerdi',
  6: 'Sabato',
};
